import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";

import { Box, Grid2, Stack, Typography } from "@mui/material";

import {
  IconAlignBoxBottomCenter,
  IconCreditCard,
  IconCurrencyReal,
  IconWallet,
} from "@tabler/icons-react";

import { reportFinance } from "../services/reports";
import { formatPrice } from "../helpers/format";

import BarChart from "./CharComponent";
import BoxLoading from "./BoxLoading";

import BoxIcon from "../assets/png/Boxing.png";

interface MonthlyAmount {
  ano_mes: string;
  total_value: number;
  total_pix?: number;
  total_creditcard?: number;
  total_debitaccount: number;
  total_cash: number;
}

const InvoicingComponent = () => {
  const currentMonth = moment().format("YYYY-MM");
  const [selectedMonth, setSelectedMonth] = useState<string | null>(
    currentMonth
  );
  const [filters] = useState({
    startDate: moment().subtract(1, "year").toDate(),
    endDate: moment().toDate(),
  });

  const {
    data,
    isLoading,
    isFetching,
  }: {
    data: {
      amountByMonth: [];
      amountIn: number;
    };
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  } = useQuery({
    queryKey: ["reportFinance"],
    queryFn: async () => await reportFinance(filters),
    initialData: {
      amountBillingType: [],
      amountByMonth: [],
      amountIn: 0,
      amountOut: 0,
    },
  });

  const chartData = {
    labels: data.amountByMonth.map((item: any) => item.ano_mes),
    datasets: [
      {
        label: "Faturamento total",
        data: data.amountByMonth.map((item: any) => item.total_value),
        backgroundColor: "#0088FE",
        maxBarThickness: 30,
      },
    ],
  };

  useEffect(() => {
    if (data.amountByMonth && data.amountByMonth.length > 0) {
      const last = data?.amountByMonth[data?.amountByMonth.length - 1] as any;
      setSelectedMonth(last?.ano_mes);
    }
  }, [data.amountByMonth]);

  const selectedData = data.amountByMonth.find(
    (item: any) => item.ano_mes === selectedMonth
  ) as MonthlyAmount | undefined;

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={{ xs: 12, md: 12 }}>
        <Stack sx={styles.stackStyle} justifyContent={"space-between"}>
          <Stack sx={styles.stackStyle}>
            <IconAlignBoxBottomCenter size={18} style={styles.iconStyle} />
            <Typography>Faturamento</Typography>
          </Stack>
          <Typography fontWeight={700}>
            <Typography
              component="span"
              color={(data?.amountIn ?? 0) >= 0 ? "success.main" : "error.main"}
            >
              {formatPrice(data?.amountIn ?? 0)}
            </Typography>{" "}
            <Typography component="span">nos últimos 12 meses</Typography>
          </Typography>
        </Stack>
      </Grid2>

      {isLoading || isFetching ? (
        <Grid2 size={12}>
          <BoxLoading loading>{null}</BoxLoading>
        </Grid2>
      ) : data.amountByMonth.length > 0 ? (
        <Grid2 size={12}>
          <Box
            sx={{
              backgroundColor: "#1E1E1E",
              padding: 2,
              borderRadius: 2,
              boxShadow: 2,
            }}
          >
            <BarChart
              data={chartData}
              title=""
              selectedMonth={selectedMonth ?? undefined}
              onBarClick={(month) => setSelectedMonth(month)}
            />
          </Box>
        </Grid2>
      ) : (
        <Grid2 size={12}>
          <Stack sx={{ width: "100%", alignItems: "center" }}>
            <img width={200} height={200} src={BoxIcon} alt="box-icon" />
            <Typography
              fontSize={18}
              fontWeight={700}
              color="secondary.contrastText"
            >
              Sem resultados obtidos
            </Typography>
          </Stack>
        </Grid2>
      )}

      <Grid2 container size={12} spacing={2}>
        {[
          {
            id: 1,
            title: `Faturamento ( ${moment(
              selectedData?.ano_mes,
              "YYYY-MM"
            ).format("MMMM [de] YYYY")} )`,
            value: formatPrice(selectedData?.total_value ?? 0),
            icon: <IconWallet color="green" />,
          },
        ].map((item) => (
          <Grid2 key={item.id} size={{ xs: 12 }}>
            <Box
              sx={{
                backgroundColor: "#1C1C1C",
                borderRadius: 2,
                padding: 2,
                boxShadow: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                textAlign: "center",
              }}
            >
              <Stack flexDirection={"row"} gap={1}>
                <Box>{item.icon}</Box>
                <Typography fontSize={20} fontWeight={700}>
                  {item.value}
                </Typography>
              </Stack>
              <Typography fontSize={12} fontWeight={500} color="text.secondary">
                {item.title}
              </Typography>
            </Box>
          </Grid2>
        ))}
        {[
          {
            id: 1,
            title: "Cartão de Crédito",
            value: formatPrice(selectedData?.total_creditcard ?? 0),
            icon: <IconCreditCard />,
          },
          {
            id: 2,
            title: "Pix",
            value: formatPrice(selectedData?.total_pix ?? 0),
            icon: <IconCurrencyReal />,
          },
          {
            id: 3,
            title: "Dinheiro",
            value: formatPrice(selectedData?.total_cash ?? 0),
            icon: <IconCurrencyReal />,
          },
        ].map((item) => (
          <Grid2 key={item.id} size={{ xs: 12, sm: 6, md: 4 }}>
            <Box
              sx={{
                backgroundColor: "#1C1C1C",
                borderRadius: 2,
                padding: 2,
                boxShadow: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                textAlign: "center",
              }}
            >
              <Typography fontSize={20} fontWeight={700}>
                {item.value}
              </Typography>
              <Typography fontSize={12} fontWeight={500} color="text.secondary">
                {item.title}
              </Typography>
            </Box>
          </Grid2>
        ))}
      </Grid2>
    </Grid2>
  );
};

const styles = {
  stackStyle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 1,
    padding: 1,
    borderRadius: 2,
    backgroundColor: "#1C1C1C",
  },
  iconStyle: {
    color: "#7600ED",
  },
};

export default InvoicingComponent;
